import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { MessageSquare, ExternalLink } from 'lucide-react';

interface ClarificationPopupProps {
  workerId: string;
}

interface PendingClarification {
  notificationId: string;
  reportId: string;
  message: string;
  supervisorQuestion: string;
}

export const ClarificationPopup = ({ workerId }: ClarificationPopupProps) => {
  const [pendingClarification, setPendingClarification] = useState<PendingClarification | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (workerId) {
      checkForPendingClarifications();
      
      // Set up realtime subscription
      const channel = supabase
        .channel('clarification_popup')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `recipient_id=eq.${workerId}`
          },
          (payload) => {
            const notif = payload.new as any;
            if (notif.notification_type === 'needs_clarification' && notif.data?.needs_response) {
              setPendingClarification({
                notificationId: notif.id,
                reportId: notif.data.report_id,
                message: notif.message,
                supervisorQuestion: notif.data.supervisor_question || notif.message
              });
              setIsOpen(true);
            }
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [workerId]);

  const checkForPendingClarifications = async () => {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('recipient_id', workerId)
      .eq('notification_type', 'needs_clarification')
      .eq('is_read', false)
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) {
      console.error('Error fetching clarifications:', error);
      return;
    }

    if (data && data.length > 0) {
      const notif = data[0];
      const notifData = notif.data as Record<string, unknown> | null;
      if (notifData?.needs_response) {
        setPendingClarification({
          notificationId: notif.id,
          reportId: String(notifData.report_id || ''),
          message: notif.message,
          supervisorQuestion: String(notifData.supervisor_question || notif.message)
        });
        setIsOpen(true);
      }
    }
  };

  const handleGoToReport = async () => {
    if (pendingClarification) {
      // Mark notification as read
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', pendingClarification.notificationId);
      
      setIsOpen(false);
      navigate(`/report/${pendingClarification.reportId}`);
    }
  };

  const handleDismiss = async () => {
    setIsOpen(false);
    // Don't mark as read - they can respond later from notification bell
  };

  if (!pendingClarification) return null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <MessageSquare className="h-5 w-5" />
            Supervisor Butuh Info Tambahan
          </DialogTitle>
          <DialogDescription>
            Supervisor meminta informasi tambahan untuk laporan Anda.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4 rounded-lg">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-1">
              Pertanyaan dari Supervisor:
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-300">
              {pendingClarification.supervisorQuestion}
            </p>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={handleDismiss} className="w-full sm:w-auto">
            Nanti Saja
          </Button>
          <Button onClick={handleGoToReport} className="w-full sm:w-auto gap-2">
            <ExternalLink className="h-4 w-4" />
            Buka Laporan & Jawab
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
